import React, { useState, useRef, useEffect } from 'react';
import { Bell, X, CheckCheck, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../context/NotificationContext';

const TYPE_COLORS = {
  overdue: 'bg-red-100 text-red-700',
  due_soon: 'bg-orange-100 text-orange-700',
  dsc: 'bg-yellow-100 text-yellow-700',
  invoice: 'bg-blue-100 text-blue-700',
  task: 'bg-purple-100 text-purple-700',
  completion: 'bg-green-100 text-green-700',
  system: 'bg-gray-100 text-gray-700',
};

const TYPE_ICONS = {
  overdue: '🔴', due_soon: '🟠', dsc: '🔑', invoice: '💰',
  task: '📋', completion: '✅', system: '⚙️',
};

export default function NotificationBell({ compact = false }) {
  const { count, notifications, markRead, markAllRead, remove } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleClick = (n) => {
    markRead(n.id);
    if (n.link) { navigate(n.link); setOpen(false); }
  };

  const timeAgo = (dt) => {
    const diff = (Date.now() - new Date(dt)) / 60000;
    if (diff < 1) return 'just now';
    if (diff < 60) return `${Math.round(diff)}m ago`;
    if (diff < 1440) return `${Math.round(diff/60)}h ago`;
    return `${Math.round(diff/1440)}d ago`;
  };

  if (compact) {
    return (
      <div className="relative inline-flex">
        <Bell size={20} />
        {count > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">{count > 9 ? '9+' : count}</span>}
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(o => !o)}
        className="relative p-2 rounded-lg hover:bg-[#264873] transition-colors">
        <Bell size={20} className="text-white" />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
            <span className="font-semibold text-gray-800 text-sm">Notifications {count > 0 && <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full ml-1">{count} new</span>}</span>
            <div className="flex items-center gap-2">
              {count > 0 && (
                <button onClick={markAllRead} title="Mark all read"
                  className="text-xs text-[#1e3a5f] hover:text-[#f0a500] flex items-center gap-1">
                  <CheckCheck size={14} /> All read
                </button>
              )}
              <button onClick={() => { navigate('/notifications'); setOpen(false); }}
                className="text-xs text-gray-400 hover:text-gray-600" title="View all">
                <ExternalLink size={14} />
              </button>
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto divide-y divide-gray-50">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center py-8 text-gray-400">
                <Bell size={28} className="mb-2 opacity-30" />
                <p className="text-sm">No notifications</p>
              </div>
            ) : (
              notifications.slice(0, 15).map(n => (
                <div key={n.id}
                  className={`flex gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors ${!n.is_read ? 'bg-blue-50/50' : ''}`}
                  onClick={() => handleClick(n)}>
                  <div className="flex-shrink-0 text-base mt-0.5">{TYPE_ICONS[n.type] || '🔔'}</div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-xs font-semibold truncate ${!n.is_read ? 'text-gray-900' : 'text-gray-600'}`}>{n.title}</div>
                    {n.message && <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.message}</div>}
                    <div className="text-xs text-gray-400 mt-1">{timeAgo(n.created_at)}</div>
                  </div>
                  {!n.is_read && <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-2" />}
                  <button onClick={e => { e.stopPropagation(); remove(n.id); }}
                    className="text-gray-200 hover:text-red-400 flex-shrink-0 mt-0.5">
                    <X size={12} />
                  </button>
                </div>
              ))
            )}
          </div>

          <div className="border-t border-gray-100 px-4 py-2 bg-gray-50">
            <button onClick={() => { navigate('/notifications'); setOpen(false); }}
              className="text-xs text-[#1e3a5f] hover:text-[#f0a500] w-full text-center">
              View all notifications →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
